import React from "react";
import { X, AlertCircle, Check, Clock, Loader, FileText } from "lucide-react";
import { getWorkflowApprovalLabel } from "../Constant/EmployeeActions.config";

const ConfirmModal = ({
  t,
  styles,
  theme,
  isRTL,
  language,
  pendingAction,
  isSubmitting,
  getEmployeeName,
  workflowDefinitions,
  onConfirm,
  onClose,
  hideSalary = false,
}) => {
  return (
    <div style={styles.modal} onClick={onClose}>
      <div
        style={{ ...styles.modalContent, maxWidth: "min(90vw, 600px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={styles.modalHeader}>
          <div
            style={{
              display: "flex",
              flexDirection: isRTL ? "row" : "row",
              alignItems: "center",
            }}
          >
            <div
              style={{
                ...styles.modalIconWrapper,
                background: pendingAction.actionConfig.color,
              }}
            >
              <AlertCircle size={26} color="white" />
            </div>
            <h2 style={styles.modalTitle}>{t.confirmAction}</h2>
          </div>
          <button
            style={styles.closeButton}
            onClick={onClose}
            className="enhanced-button-hover"
          >
            <X size={24} />
          </button>
        </div>

        <div style={{ ...styles.alertBox, ...styles.warningAlert }}>
          <AlertCircle size={22} />
          <span
            style={{
              fontWeight: "700",
              fontSize: "clamp(13px, 1.9vw, 15px)",
            }}
          >
            {t.reviewBeforeConfirm}
          </span>
        </div>

        <div style={styles.infoBox}>
          <h3
            style={{
              fontSize: "clamp(16px, 2.8vw, 18px)",
              fontWeight: "800",
              marginBottom: "16px",
              color: theme === "dark" ? "#ffffff" : "#0f172a",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            {t.actionDetails}
          </h3>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>{t.actionType}</span>
            <span
              style={{
                ...styles.badge,
                background: pendingAction.actionConfig.bgColor,
                border: `1px solid ${pendingAction.actionConfig.borderColor}`,
                color: theme === "dark" ? "#ffffff" : "#0f172a",
              }}
            >
              {t[pendingAction.actionConfig.labelKey]}
            </span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>{t.employee}</span>
            <span style={styles.infoValue}>
              {getEmployeeName(pendingAction.employee)}
            </span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>{t.infinityCode}</span>
            <span style={styles.infoValue}>
              {pendingAction.employee.clientCode || pendingAction.employee.infinityCode}
            </span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>{t.effectiveDate}</span>
            <span style={styles.infoValue}>
              {new Date(
                pendingAction.formData.effectiveDate
              ).toLocaleDateString()}
            </span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>{t.reason}</span>
            <span
              style={{
                ...styles.infoValue,
                fontSize: "clamp(12px, 1.7vw, 14px)",
              }}
            >
              {pendingAction.formData.reason}
            </span>
          </div>
          {!hideSalary && pendingAction.actionConfig.id === "salary_change" &&
            [
              [isRTL ? "صافي الراتب" : "Net salary", pendingAction.employee?.netSalary, pendingAction.formData?.newSalary],
              [isRTL ? "الراتب الإجمالي" : "Gross salary", pendingAction.employee?.grossSalary, pendingAction.formData?.newGrossSalary],
              [isRTL ? "الأجر التأميني" : "Insurance salary", pendingAction.employee?.insuranceSalary, pendingAction.formData?.newInsuranceSalary],
            ]
              .filter(([, , next]) => next !== "" && next !== null && next !== undefined && !Number.isNaN(Number(next)))
              .map(([label, prev, next]) => {
                const changed = prev === null || prev === undefined || Math.abs(Number(next) - Number(prev)) > 0.009;
                const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }));
                return (
                  <div key={label} style={styles.infoRow}>
                    <span style={styles.infoLabel}>{label}</span>
                    <span style={styles.infoValue}>
                      {changed ? (
                        <>
                          {fmt(prev)} {isRTL ? "←" : "→"}{" "}
                          <strong style={{ color: Number(next) >= Number(prev ?? 0) ? "#10b981" : "#ef4444" }}>{fmt(next)}</strong>
                        </>
                      ) : (
                        fmt(next)
                      )}
                    </span>
                  </div>
                );
              })}
          {pendingAction.actionConfig.requiresApproval && (
            <div
              style={{
                ...styles.infoRow,
                borderBottom: "none",
                marginBottom: 0,
                paddingBottom: 0,
              }}
            >
              <span style={styles.infoLabel}>{t.approvalRequired}</span>
              <span
                style={{
                  ...styles.badge,
                  background: "rgba(245, 158, 11, 0.15)",
                  border: "1px solid rgba(245, 158, 11, 0.4)",
                  color: theme === "dark" ? "#fbbf24" : "#d97706",
                }}
              >
                <Clock size={14} />
                {getWorkflowApprovalLabel(
                  pendingAction.actionConfig.id,
                  workflowDefinitions,
                  language,
                  t,
                  pendingAction.actionConfig.approvalLevelKey,
                  pendingAction.employee?.department?.name ?? pendingAction.employee?.employeeType ?? null
                )}
              </span>
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: isRTL ? "row" : "row",
            gap: "14px",
            marginTop: "clamp(24px, 3.8vw, 32px)",
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              flex: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              padding: "12px 16px",
              borderRadius: "8px",
              border: theme === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.12)",
              background: "transparent",
              color: theme === "dark" ? "#fff" : "#0f172a",
              fontSize: "14px",
              fontWeight: 600,
              cursor: isSubmitting ? "not-allowed" : "pointer",
              opacity: isSubmitting ? 0.6 : 1,
            }}
          >
            <X size={18} /> {t.cancel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting}
            style={{
              flex: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              padding: "12px 16px",
              borderRadius: "8px",
              border: "none",
              background: pendingAction.actionConfig.color,
              color: "#fff",
              fontSize: "14px",
              fontWeight: 600,
              cursor: isSubmitting ? "not-allowed" : "pointer",
              opacity: isSubmitting ? 0.6 : 1,
            }}
          >
            {isSubmitting ? (
              <>
                <Loader size={18} style={styles.loadingSpinner} />{" "}
                {t.processing}
              </>
            ) : (
              <>
                <Check size={18} /> {t.confirmSubmit}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;